import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  AthleteAdminBreadcrumbNav,
  AthleteAdminNavState,
  getTeamMemberBreadcrumbLabel,
  navigateToTeamMemberFromBreadcrumb,
  useAdminAthleteDisplayName,
  useAthleteStaffForAdminBreadcrumb,
} from './AthleteAdminBreadcrumbNav';
import { isAthlete, isGuardian } from '../utils/auth';
import { resolveViewedAthleteProfileId } from '../utils/viewedAthlete';
import { fetchAthleteSessionsApi, deleteSessionApi } from '../services/sessionsService';
import { SessionResponse } from '../types/sessions';
import LogSessionModal from './LogSessionModal';
import ViewSessionModal from './ViewSessionModal';
import DeleteSessionModal from './DeleteSessionModal';
import Pagination from './Pagination';
import '../styles/athlete-admin-layout.css';
import '../styles/athlete-admin-scaled-wrapper.css';
import '../styles/sessions-page.css';
import '../styles/pagination.css';
import '../styles/athlete-admin-page-spacing.css';

const PAGE_SIZE = 6;

const SessionsPage: React.FC = () => {
  const outletContext = useOutletContext<{ staffDisplayName: string } | null>();
  const staffDisplayName = outletContext?.staffDisplayName || '';
  const { athleteId: athleteIdParam } = useParams<{ athleteId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as AthleteAdminNavState | null;

  const targetAthleteId = useMemo(() => {
    const resolved = resolveViewedAthleteProfileId(athleteIdParam);
    if (resolved) {
      const n = parseInt(resolved, 10);
      if (!Number.isNaN(n) && n > 0) return n;
      return -1;
    }
    const stored = localStorage.getItem('athleteId');
    if (!stored) return -1;
    const n = parseInt(stored, 10);
    if (!Number.isNaN(n) && n > 0) return n;
    return -1;
  }, [athleteIdParam]);

  const isAdminViewingAthlete = !isAthlete() && !isGuardian() && !!athleteIdParam;
  const idInvalid = targetAthleteId < 0;

  const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
    athleteIdParam,
    Boolean(isAdminViewingAthlete && athleteIdParam && !idInvalid)
  );

  const [sessions, setSessions] = useState<SessionResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(() => !idInvalid);
  const [error, setError] = useState<string | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [viewSession, setViewSession] = useState<SessionResponse | null>(null);
  const [editSession, setEditSession] = useState<SessionResponse | null>(null);
  const [deleteSessionId, setDeleteSessionId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<number | null>(null);
  const [coachData, setCoachData] = useState<{
    id?: string;
    firstName?: string;
    lastName?: string;
  } | null>(null);
  const sessionsListBodyRef = useRef<HTMLDivElement>(null);

  const isAthleteUser = isAthlete();
  /** Only admin/staff may log, edit, or delete sessions — not athletes or guardians. */
  const canMutateSessions = !isAthleteUser && !isGuardian();

  useEffect(() => {
    if (navState?.teamMemberName) {
      const nameParts = navState.teamMemberName.split(' ');
      setCoachData({
        id: navState.teamMemberId,
        firstName: nameParts[0] || navState.teamMemberName,
        lastName: nameParts.slice(1).join(' ') || '',
      });
    } else {
      setCoachData(null);
    }
  }, [navState?.teamMemberName, navState?.teamMemberId]);

  const handleNavigateToTeamMember = () => {
    navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
  };

  const normalizeUrl = (url: string): string => {
    if (!url || !url.trim()) return '';

    const trimmedUrl = url.trim();

    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }

    return `https://${trimmedUrl}`;
  };

  const loadSessions = useCallback(
    async (page = 0) => {
      if (targetAthleteId < 0) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchAthleteSessionsApi(targetAthleteId, page, PAGE_SIZE);
        setSessions(data.content);
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages);
        setCurrentPage(page);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sessions');
      } finally {
        setLoading(false);
      }
    },
    [targetAthleteId]
  );

  useEffect(() => {
    if (targetAthleteId < 0) {
      return;
    }
    loadSessions(0);
  }, [targetAthleteId, loadSessions]);

  const handlePageChange = (page: number) => {
    loadSessions(page);
    sessionsListBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogSession = () => {
    setEditSession(null);
    setIsLogModalOpen(true);
  };

  const handleEditSession = (session: SessionResponse) => {
    setEditSession(session);
    setIsLogModalOpen(true);
  };

  const handleViewSession = (session: SessionResponse) => {
    setViewSession(session);
  };

  const handleDeleteSession = (sessionId: number) => {
    setSessionToDelete(sessionId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    try {
      setDeleteSessionId(sessionToDelete);
      await deleteSessionApi(targetAthleteId, sessionToDelete);
      const newPage = sessions.length === 1 && currentPage > 0 ? currentPage - 1 : currentPage;
      await loadSessions(newPage);
      setShowDeleteModal(false);
      setSessionToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete session');
    } finally {
      setDeleteSessionId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSessionToDelete(null);
  };

  const handleModalClose = () => {
    setIsLogModalOpen(false);
    setEditSession(null);
    loadSessions(currentPage);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const teamMemberLabel = getTeamMemberBreadcrumbLabel(
    coachData,
    navState?.teamMemberName,
    athleteStaffName ?? undefined
  );

  const athleteLabel = useAdminAthleteDisplayName(athleteIdParam, navState?.athleteDisplayName);

  const adminBreadcrumb = isAdminViewingAthlete ? (
    <AthleteAdminBreadcrumbNav
      pageLabel="Sessions"
      teamMemberLabel={teamMemberLabel}
      onTeamMemberClick={handleNavigateToTeamMember}
      athleteLabel={athleteLabel}
      athleteProfileId={athleteIdParam}
      data-testid="sessions-breadcrumb"
    />
  ) : null;

  if (idInvalid) {
    return (
      <div className="sessions-page">
        {adminBreadcrumb}
        <div className="sessions-error" data-testid="sessions-error">
          Athlete ID not found in localStorage
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sessions-page">
        {adminBreadcrumb}
        {isAdminViewingAthlete ? (
          <div className="sessions-content-panel">
            <div className="sessions-loading">
              <div className="sessions-spinner" />
            </div>
          </div>
        ) : (
          <div className="sessions-loading">
            <div className="sessions-spinner" />
          </div>
        )}
      </div>
    );
  }

  const mainBody = (
    <>
      {sessions.length > 0 && (
        <div className={`sessions-header${isAdminViewingAthlete ? ' sessions-header--admin' : ''}`}>
          {!isAdminViewingAthlete && <h1 className="sessions-title">Sessions</h1>}
          {canMutateSessions && (
            <button
              className="log-session-button"
              onClick={handleLogSession}
              data-testid="log-session-button"
            >
              <svg
                className="log-session-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Log session
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="sessions-error" data-testid="sessions-error">
          {error}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="sessions-empty-state" data-testid="sessions-empty-state">
          <div className="sessions-empty-hero">
            <svg
              className="sessions-empty-icon"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="20" y="30" width="80" height="60" rx="8" fill="#F3F4F6" />
              <path
                d="M35 50h50M35 65h40M35 80h30"
                stroke="#D1D5DB"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx="90" cy="40" r="15" fill="#FEE2E2" />
              <path d="M90 33v14M90 54v2" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <div className="sessions-empty-copy">
              <h2 className="sessions-empty-title">No sessions yet</h2>
              <p className="sessions-empty-description">
                {isAthleteUser
                  ? "This athlete hasn't worked with an admin or staff member yet. Sessions will appear here once they are logged."
                  : "This athlete hasn't worked with an admin or staff member yet. Start by logging your first session to begin tracking progress and insights."}
              </p>
              {canMutateSessions && (
                <button
                  className="log-session-button"
                  onClick={handleLogSession}
                  data-testid="log-session-empty-button"
                >
                  <svg
                    className="log-session-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  Log session
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="sessions-section">
          <div ref={sessionsListBodyRef} className="sessions-list-body">
            <div className="sessions-list" data-testid="sessions-list">
              {sessions.map((session) => (
                <div key={session.id} className="session-card" data-testid="session-card">
                  {session.staffProfileImageUrl ? (
                    <img
                      src={session.staffProfileImageUrl}
                      alt={session.staffName}
                      className="session-staff-avatar"
                    />
                  ) : (
                    <div className="session-staff-avatar-placeholder">
                      {getInitials(session.staffName)}
                    </div>
                  )}

                  <div className="session-info">
                    <div className="session-info-item">
                      <span className="session-info-label">Staff</span>
                      <span className="session-info-value">{session.staffName}</span>
                    </div>

                    <div className="session-info-item">
                      <span className="session-info-label">Date</span>
                      <span className="session-info-value">{formatDate(session.date)}</span>
                    </div>

                    <div className="session-info-item">
                      <span className="session-info-label">Hours</span>
                      <span className="session-info-value">{session.hours}</span>
                    </div>

                    <div className="session-info-item">
                      <span className="session-info-label">
                        <svg
                          className="session-link-icon"
                          style={{ display: 'inline', marginRight: '4px' }}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                        Recording
                      </span>
                      {session.recordingLink ? (
                        <a
                          href={normalizeUrl(session.recordingLink)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="session-link"
                          data-testid="session-recording-link"
                        >
                          {session.recordingLink.length > 25
                            ? `${session.recordingLink.substring(0, 25)}...`
                            : session.recordingLink}
                        </a>
                      ) : (
                        <span className="session-info-value">—</span>
                      )}
                    </div>

                    <div className="session-info-item">
                      <span className="session-info-label">
                        <svg
                          className="session-link-icon"
                          style={{ display: 'inline', marginRight: '4px' }}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                        Notes
                      </span>
                      {session.notesLink ? (
                        <a
                          href={normalizeUrl(session.notesLink)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="session-link"
                          data-testid="session-notes-link"
                        >
                          {session.notesLink.length > 25
                            ? `${session.notesLink.substring(0, 25)}...`
                            : session.notesLink}
                        </a>
                      ) : (
                        <span className="session-info-value">—</span>
                      )}
                    </div>

                    <div className="session-info-item">
                      <span className="session-info-label">Notes</span>
                      <span className="session-notes-preview">
                        {session.notes && session.notes.length > 30
                          ? `${session.notes.substring(0, 30)}...`
                          : session.notes || '—'}
                      </span>
                    </div>
                  </div>

                  <div className="session-actions">
                    <button
                      className="session-action-button"
                      onClick={() => handleViewSession(session)}
                      title="View session"
                      data-testid="view-session-button"
                    >
                      <svg
                        className="session-action-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>

                    {canMutateSessions && (
                      <>
                        <button
                          className="session-action-button"
                          onClick={() => handleEditSession(session)}
                          title="Edit session"
                          data-testid="edit-session-button"
                        >
                          <svg
                            className="session-action-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>

                        <button
                          className="session-action-button delete"
                          onClick={() => handleDeleteSession(session.id)}
                          title="Delete session"
                          disabled={deleteSessionId === session.id}
                          data-testid="delete-session-button"
                        >
                          <svg
                            className="session-action-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );

  const hasListedSessions = sessions.length > 0;

  const paginationFooter =
    sessions.length > 0 && totalElements > 0 ? (
      <div className="sessions-pagination-footer" data-testid="sessions-pagination-footer">
        <Pagination
          currentPage={currentPage}
          totalPages={Math.max(totalPages, 1)}
          totalElements={totalElements}
          onPageChange={handlePageChange}
          pageSize={PAGE_SIZE}
        />
      </div>
    ) : null;

  return (
    <div className={`sessions-page${hasListedSessions ? ' sessions-page--listed' : ''}`}>
      {adminBreadcrumb}
      {isAdminViewingAthlete ? (
        <div className={`sessions-content-panel${hasListedSessions ? ' sessions-content-panel--listed' : ''}`}>
          {mainBody}
        </div>
      ) : (
        mainBody
      )}
      {paginationFooter}

      {isLogModalOpen && (
        <LogSessionModal
          athleteId={targetAthleteId}
          session={editSession}
          staffDisplayName={staffDisplayName || ''}
          onClose={handleModalClose}
        />
      )}

      {viewSession && (
        <ViewSessionModal session={viewSession} onClose={() => setViewSession(null)} />
      )}

      {showDeleteModal && (
        <DeleteSessionModal onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} />
      )}
    </div>
  );
};

export default SessionsPage;
